import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import search from "../../../images/search.svg";
import "./IOSPicker.css";

const IOSPicker = ({
    options = [],
    selectedValue,
    selectedName,
    onSelect,
    onSelectCode,
    openModal,
    handleClose,
    title,
    keyCode = "",
    keyName = "city_name", // Added keyName prop
    noResult = "No Result Found",
}) => {
    const initialIndex = options.findIndex((option) => option[keyName] === selectedValue) ?? 0;
    const [activeIndex, setActiveIndex] = useState(initialIndex);
    const [bounceDirection, setBounceDirection] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);
    const pickerRef = useRef(null);
    const startY = useRef(null);
    const lastY = useRef(null);
    const swipeSpeed = useRef(0);

    useEffect(() => {
        if (openModal) {
            setIsModalOpen(true);

            // Delay overlay visibility by 1 second
            const overlayTimeout = setTimeout(() => {
                setShowOverlay(true);
            }, 280);

            return () => clearTimeout(overlayTimeout); // Cleanup timeout on unmount
        }
    }, [openModal]);

    const debounceTimeout = useRef(null);
    const handleSearchChange = (e) => {
        const value = e.target.value.trim();

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            setSearchTerm(value);
        }, 300);
    };

    const filteredOptions = options?.filter((option) => option[keyName]?.toLowerCase()?.includes(searchTerm?.toLowerCase()));

    useEffect(() => {
        if (filteredOptions.length > 0) {
            setActiveIndex(!searchTerm && initialIndex != -1 ? initialIndex : 0);
            onSelect(!searchTerm && selectedName ? selectedName : filteredOptions[0][keyName]); // Select first option by default
            if (keyCode) {
                onSelectCode(!searchTerm && selectedName ? selectedName : filteredOptions[0][keyCode]);
            }
        } else {
            setActiveIndex(!searchTerm && initialIndex != -1 ? initialIndex : 0);
            onSelect(selectedName ? selectedName : ""); // Select first option by default
            if (keyCode) {
                onSelectCode("");
            }
        }
    }, [searchTerm, filteredOptions.length]);

    const handleWheel = (e) => {
        e.preventDefault();
        if (filteredOptions.length === 0) return;
        const direction = e.deltaY > 0 ? 1 : -1;
        updateIndex(direction);
    };

    const handleTouchStart = (e) => {
        if (filteredOptions.length === 0) return;
        startY.current = e.touches[0].clientY;
        lastY.current = startY.current;
        swipeSpeed.current = 0;
    };

    const handleTouchMove = (e) => {
        if (startY.current === null || filteredOptions.length === 0) return;
        const currentY = e.touches[0].clientY;
        const direction = startY.current - currentY;

        e.preventDefault();

        swipeSpeed.current = lastY.current - currentY;
        lastY.current = currentY;

        if (Math.abs(direction) > 30) {
            updateIndex(direction > 0 ? Math.ceil(swipeSpeed.current / 12) : Math.floor(swipeSpeed.current / 12));
            startY.current = currentY;
        }
    };

    const handleTouchEnd = () => {
        startY.current = null;
    };

    const updateIndex = (direction) => {
        if (filteredOptions.length === 0) return;

        let newIndex = activeIndex + direction;
        const maxIndex = filteredOptions.length - 1;

        if (filteredOptions.length === 1) {
            newIndex = 0;
        } else if (newIndex < 0) {
            newIndex = 0;
            setBounceDirection(-1);
        } else if (newIndex > maxIndex) {
            newIndex = maxIndex;
            setBounceDirection(1);
        } else {
            setBounceDirection(0);
        }

        if (newIndex !== activeIndex) {
            setActiveIndex(newIndex);
            onSelect(filteredOptions[newIndex][keyName]); // Update selected option
            if (keyCode) {
                onSelectCode(filteredOptions[newIndex][keyCode]);
            }
            if (navigator.vibrate) {
                navigator.vibrate(30);
            }
        }
    };

    useEffect(() => {
        const handleKeyboardShow = (e) => {
            const keyboardHeight = e.target.clientHeight || window.innerHeight * 0.3;
            const modal = document.querySelector(".ios-picker-modal");
            if (modal) {
                modal.style.transform = `translateY(-${keyboardHeight}px)`;
            }
        };

        const handleKeyboardHide = () => {
            const modal = document.querySelector(".ios-picker-modal");
            if (modal) {
                modal.style.transform = "translateY(0)";
            }
        };

        window.addEventListener("resize", () => {
            const isKeyboardVisible = window.innerHeight < window.outerHeight - 100;
            if (isKeyboardVisible) {
                handleKeyboardShow({
                    target: {
                        clientHeight: window.outerHeight - window.innerHeight,
                    },
                });
            } else {
                handleKeyboardHide();
            }
        });

        return () => {
            window.removeEventListener("resize", handleKeyboardHide);
        };
    }, []);

    useEffect(() => {
        const picker = pickerRef.current;

        if (picker) {
            picker.addEventListener("wheel", handleWheel, { passive: false });
            picker.addEventListener("touchstart", handleTouchStart, {
                passive: false,
            });
            picker.addEventListener("touchmove", handleTouchMove, {
                passive: false,
            });
            picker.addEventListener("touchend", handleTouchEnd, {
                passive: true,
            });
        }

        return () => {
            if (picker) {
                picker.removeEventListener("wheel", handleWheel);
                picker.removeEventListener("touchstart", handleTouchStart);
                picker.removeEventListener("touchmove", handleTouchMove);
                picker.removeEventListener("touchend", handleTouchEnd);
            }
        };
    }, [activeIndex, filteredOptions.length, isModalOpen]);

    useEffect(() => {
        const handleBackButton = () => {
            if (isModalOpen) {
                setIsModalOpen(false);
            }
        };

        window.addEventListener("popstate", handleBackButton);

        return () => {
            window.removeEventListener("popstate", handleBackButton);
        };
    }, [isModalOpen]);

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setShowOverlay(false);
        handleClose();
    };

    return (
        <div>
            {isModalOpen && (
                <motion.div className={`ios-picker-modal  ${isModalOpen ? "open" : "close"}`} initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ duration: 0.015 }}>
                    <div className={`${showOverlay && "ios-picker-overlay"}`} onClick={handleCloseModal} />
                    <div className="ios-picker-container bg-wafaa-white-2">
                        <button className="ios-picker-close-btn bg-wafaa-white-2 text-blue-white" onClick={handleCloseModal}>
                            ✕
                        </button>
                        <div
                            className="ios-picker-search"
                            style={{
                                position: "relative",
                            }}
                        >
                            <input
                                type="text"
                                defaultValue={searchTerm}
                                onChange={handleSearchChange}
                                placeholder={`Search ${title ? title : "..."}`}
                                className="ios-picker-search-input text-blue-white"
                                style={{
                                    paddingLeft: "37px", // Add padding to the left for the icon
                                    paddingBlock: "10px",
                                    background: "transparent",
                                    borderRadius: "8px",
                                    border: "1px solid #ccc",
                                    transition: "border-color 0.1s ease-in-out",
                                    width: "100%", // Full width of the parent
                                }}
                            />
                            <span
                                className="position-absolute"
                                style={{
                                    left: "10px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    pointerEvents: "none",
                                    color: "#999",
                                    fontSize: "14px",
                                }}
                            >
                                <img src={search} alt="search" width={"25px"} height={"25px"} style={{ opacity: "0.75" }} />
                            </span>
                        </div>
                        <div className="ios-picker-window" ref={pickerRef}>
                            <div className="ios-picker-highlight" />
                            <motion.ul
                                className="ios-picker-list"
                                animate={{
                                    y: filteredOptions.length > 0 ? -activeIndex * 50 + 99 + bounceDirection * 10 : 0,
                                }}
                                transition={{
                                    type: "spring",
                                    stiffness: 400,
                                    damping: 32,
                                }}
                                onAnimationComplete={() => {
                                    if (bounceDirection !== 0) {
                                        setBounceDirection(0);
                                    }
                                }}
                            >
                                {filteredOptions.length > 0 ? (
                                    filteredOptions.map((option, index) => (
                                        <motion.li
                                            key={index}
                                            className={`ios-picker-item ${index === activeIndex ? "text-blue-white-bold" : ""}`}
                                            onClick={() => {
                                                onSelect(option[keyName]); // Update with selected option
                                                if (keyCode) {
                                                    onSelectCode(option[keyCode]);
                                                }
                                                setActiveIndex(index);
                                            }}
                                        >
                                            {option[keyName]} {/* Use keyName here */}
                                        </motion.li>
                                    ))
                                ) : (
                                    <li className="ios-picker-no-result">{noResult}</li>
                                )}
                            </motion.ul>
                        </div>
                    </div>
                </motion.div>
            )}
        </div>
    );
};

export default IOSPicker;
